import {
    CoreFetch,
    ExperimentApi,
    FilterMethod,
    GetProviderExperimentClaimListOrderEnum,
    GetProviderExperimentClaimListSortEnum,
    GetProviderExperimentListOrderEnum,
    GetProviderExperimentListSortEnum,
    ProviderExperiment,
    ProviderExperimentClaim,
    createRequestFilters,
    ICustomFilter,
} from '@enter/core';
import { action, observable, runInAction } from 'mobx';

export interface IExperimentsListing {
    experiments: ProviderExperiment[];
    limit?: number;
    offset?: number;
    total?: number;
}

export interface IExperimentsListingRequest {
    page?: number;
    pageSize?: number;
    filters?: ICustomFilter[];
    sort?: GetProviderExperimentListSortEnum[];
    order?: GetProviderExperimentListOrderEnum[];
}

export class ExperimentsStore {
    @observable public experiments: ProviderExperiment[] = [];
    @observable public experimentClaims: ProviderExperimentClaim[] = [];
    @observable public loading: boolean = false;
    @observable public total: number = 0;

    @action
    public async loadExperimentsList(request?: IExperimentsListingRequest): Promise<IExperimentsListing> {
        this.loading = true;
        try {
            const limit = request?.pageSize ?? 25;
            const offset = request?.page && request?.pageSize ? request.page * request.pageSize : 0;
            const sort = request?.sort && request.sort.length > 0 ? request.sort : [GetProviderExperimentListSortEnum.CREATED];
            const order = request?.order && request.order.length > 0 ? request.order : [GetProviderExperimentListOrderEnum.DESCENDING];
            const filters = request?.filters ? createRequestFilters(request.filters, true) : {};

            const raw = await CoreFetch<ExperimentApi>(ExperimentApi).getProviderExperimentListRaw({
                limit,
                offset,
                sort,
                order,
                ...filters,
            });

            const experiments = await raw.value();
            const paginationTotal = raw.raw.headers.get('pagination-total');
            const total = paginationTotal ? parseInt(paginationTotal, 10) : experiments.length;
            const resolvedTotal = Number.isNaN(total) ? experiments.length : total;

            runInAction(() => {
                this.experiments = experiments;
                this.total = resolvedTotal;
                this.loading = false;
            });

            return {
                experiments,
                limit,
                offset,
                total: resolvedTotal,
            };
        } catch (error) {
            runInAction(() => {
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    public async getExperiment(experimentId: string): Promise<ProviderExperiment> {
        try {
            // Use list API with filters, matching enter-support pattern
            const filters: ICustomFilter[] = [
                {
                    filterId: 'filterId',
                    filterMethod: FilterMethod.EQ,
                    filterType: 'String',
                    value: experimentId,
                },
            ];

            const reqFilters = createRequestFilters(filters);
            const experiments = await CoreFetch<ExperimentApi>(ExperimentApi).getProviderExperimentList({
                ...reqFilters,
                limit: 1,
            });

            if (experiments && experiments.length > 0) {
                const experiment = experiments[0];
                
                runInAction(() => {
                    // Update or add to experiments list
                    const index = this.experiments.findIndex((e) => e.id === experimentId);
                    if (index >= 0) {
                        this.experiments[index] = experiment;
                    } else {
                        this.experiments = [...this.experiments, experiment];
                    }
                });

                return experiment;
            }

            throw new Error(`Experiment with ID ${experimentId} not found`);
        } catch (error) {
            console.error('Error fetching experiment:', error);
            throw new Error(`Experiment with ID ${experimentId} not found`);
        }
    }

    @action
    public async getExperimentClaims(
        experimentId: string,
        inExperiment?: boolean
    ): Promise<ProviderExperimentClaim[]> {
        const claims = await CoreFetch<ExperimentApi>(ExperimentApi).getProviderExperimentClaimList({
            id: experimentId,
            filterIsClaimInTheExperiment: inExperiment !== undefined ? (inExperiment ? 'EQ:1' : 'EQ:0') : undefined,
            sort: [GetProviderExperimentClaimListSortEnum.CREATED],
            order: [GetProviderExperimentClaimListOrderEnum.DESCENDING],
        });

        runInAction(() => {
            this.experimentClaims = claims;
        });

        return claims;
    }
}

